import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Loader2, Pencil, Download, Send, Printer, Mail, MessageSquare, X, Eye, FileSignature, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import FormFiller from '@/components/forms/FormFiller';
import { useFormStatusFlows, formStatusLabel } from '@/hooks/useFormStatusFlows';

/**
 * FormViewerDialog — Alian (Excel "Office Work Flow (2)", hojas Permit
 * Application / Completion Report / NOC / NTO): al hacer clic en una forma se
 * abre el PDF oficial YA RELLENO a pantalla completa, sin paneles a los
 * costados, con los botones EDIT · View In PDF (Download) · SEND arriba y su
 * propia barra de estados (configurable por forma en Settings > Job Status).
 */
export default function FormViewerDialog({ open, onClose, permit, form, editChildren, onChanged }) {
  const { t } = useTranslation();
  const { flowFor } = useFormStatusFlows();
  const [mode, setMode] = useState('preview');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [instanceId, setInstanceId] = useState(null);
  const [template, setTemplate] = useState(null);
  const [officialMeta, setOfficialMeta] = useState(null);
  const [status, setStatus] = useState('draft');
  const [busy, setBusy] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsTo, setSmsTo] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signLink, setSignLink] = useState('');
  const [signCopied, setSignCopied] = useState(false);
  const [signSmsTo, setSignSmsTo] = useState('');
  const [signSending, setSignSending] = useState(false);
  const blobUrlRef = useRef(null);
  const blobDataRef = useRef(null);

  const isStructured = form?.kind === 'structured';
  const pathPart = form?.formKey === 'well_application' ? 'well-application' : 'completion-report';
  const pdfPath = form?.formKey === 'well_application' ? 'application-pdf' : 'completion-report-pdf';
  const flowId = isStructured
    ? (form?.formKey === 'well_application' ? 'structured_permit_application' : 'structured_completion_report')
    : form?.templateId;
  const statuses = flowFor(flowId);

  // Reset per-form state on open.
  useEffect(() => {
    if (!open || !form) return;
    setMode('preview');
    setPdfError(false);
    setInstanceId(form.instanceId || null);
    if (isStructured) {
      setStatus((permit?.[`${form.formKey}_status`]?.status || 'draft').toLowerCase());
      api.get(`/permits/${permit.id}/${pathPart}/form-meta`)
        .then(res => setOfficialMeta(res.data))
        .catch(() => setOfficialMeta({ official: false }));
    } else {
      setOfficialMeta(null);
      api.get(`/custom-forms/templates/${form.templateId}`)
        .then(res => setTemplate(res.data))
        .catch(() => setTemplate(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.templateId, form?.formKey, form?.instanceId]);

  // Ensure a template form has an instance (creating it = "New Form": queda
  // registrada en All Files con su status Draft).
  const ensureInstance = useCallback(async () => {
    if (isStructured) return null;
    if (instanceId) return instanceId;
    const res = await api.post('/custom-forms/instances', {
      template_id: form.templateId,
      linked_type: 'permit',
      linked_id: permit.id,
      field_values: {},
    });
    setInstanceId(res.data.id);
    onChanged && onChanged();
    return res.data.id;
  }, [isStructured, instanceId, form?.templateId, permit?.id, onChanged]);

  const loadPdf = useCallback(async () => {
    if (!open || !form || !permit?.id) return;
    setPdfLoading(true);
    setPdfError(false);
    try {
      let res;
      if (isStructured) {
        res = await api.get(`/permits/${permit.id}/${pdfPath}`, { responseType: 'blob' });
      } else {
        const id = await ensureInstance();
        setStatus((form.status || 'draft').toLowerCase());
        res = await api.get(`/custom-forms/instances/${id}/pdf`, { responseType: 'blob' });
      }
      if (blobUrlRef.current) window.URL.revokeObjectURL(blobUrlRef.current);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      blobDataRef.current = blob;
      const url = window.URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setPdfUrl(url);
    } catch {
      setPdfError(true);
    } finally {
      setPdfLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.templateId, form?.formKey, permit?.id, permit?.updated_at]);

  useEffect(() => {
    if (open && mode === 'preview') loadPdf();
    return () => { if (blobUrlRef.current) { window.URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, loadPdf]);

  const handleStatusClick = async (key) => {
    if (key === status) return;
    setBusy(true);
    try {
      if (isStructured) {
        await api.put(`/permits/${permit.id}/${pathPart}/status`, { status: key });
      } else {
        const id = await ensureInstance();
        await api.put(`/custom-forms/instances/${id}`, { status: key });
      }
      setStatus(key);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${(form?.name || 'form').replace(/[^a-z0-9]+/gi, '_')}.pdf`;
    a.click();
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const win = window.open(pdfUrl, '_blank');
    if (win) win.onload = () => { try { win.print(); } catch { /* bloqueado por el navegador */ } };
  };

  const handleSendEmail = async () => {
    setBusy(true);
    try {
      if (isStructured) {
        await api.post(`/permits/${permit.id}/${pathPart}/send`);
      } else {
        const id = await ensureInstance();
        await api.post(`/custom-forms/instances/${id}/send`, {});
      }
      toast.success(t('permits.battery.sendSuccess'));
      setStatus('sent');
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('permits.battery.sendError'));
    } finally {
      setBusy(false);
    }
  };

  const openSmsDialog = async () => {
    setBusy(true);
    try {
      const id = isStructured ? '' : await ensureInstance();
      const res = await api.post(`/permits/${permit.id}/forms/share-link`, {
        kind: form.kind,
        form_key: form.formKey || '',
        instance_id: id || '',
        name: form.name || '',
      });
      setSmsTo(permit?.customer_phone || '');
      setSmsBody(`Blue Well Drilling sent you a document (${form.name || 'Form'}): ${res.data.url}`);
      setSmsOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo generar el link del documento');
    } finally {
      setBusy(false);
    }
  };

  const handleSendSms = async () => {
    if (!smsTo.trim()) { toast.error('Ingrese un teléfono'); return; }
    setSmsSending(true);
    try {
      await api.post('/voice/sms', { to: smsTo.trim(), body: smsBody });
      toast.success('SMS enviado');
      setSmsOpen(false);
      setStatus('sent');
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el SMS');
    } finally {
      setSmsSending(false);
    }
  };

  // Send > Online Signature — genera un link público (mismo patrón del
  // estimado /estimate/:token) donde el cliente ve el PDF y dibuja UNA sola
  // firma (la suya). El status pasa solo a "Signed" cuando firma.
  const openSignDialog = async () => {
    setBusy(true);
    try {
      const id = isStructured ? '' : await ensureInstance();
      const res = await api.post(`/permits/${permit.id}/forms/sign-link`, {
        kind: form.kind,
        form_key: form.formKey || '',
        instance_id: id || '',
        name: form.name || '',
      });
      setSignLink(res.data.url);
      setSignSmsTo(permit?.customer_phone || '');
      setSignCopied(false);
      setSignOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo generar el link de firma');
    } finally {
      setBusy(false);
    }
  };

  const copySignLink = async () => {
    try {
      await navigator.clipboard.writeText(signLink);
      setSignCopied(true);
      setTimeout(() => setSignCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  const sendSignLinkSms = async () => {
    if (!signSmsTo.trim()) { toast.error('Ingrese un teléfono'); return; }
    setSignSending(true);
    try {
      await api.post('/voice/sms', {
        to: signSmsTo.trim(),
        body: `Blue Well Drilling necesita tu firma en "${form.name || 'Form'}": ${signLink}`,
      });
      toast.success('SMS enviado');
      setSignOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el SMS');
    } finally {
      setSignSending(false);
    }
  };

  // El Excel muestra EDIT solo en Permit Application / Completion Report;
  // NOC y NTO son PDFs sellados (View In PDF · Send). Se habilita Edit cuando
  // la forma realmente tiene campos editables.
  const canEdit = isStructured || (template?.fields?.length > 0);

  if (!form) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent
          className="max-w-[100vw] w-[100vw] h-[100vh] sm:max-w-[100vw] p-0 gap-0 rounded-none border-0 flex flex-col overflow-hidden [&>button.absolute]:hidden"
          data-testid="form-viewer-dialog"
        >
          {/* Toolbar — nombre + 3 botones (Edit / Download PDF / Send) */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 bg-white flex-shrink-0 flex-wrap">
            <h2 className="text-base font-bold text-gray-900 font-['Barlow'] mr-auto truncate" data-testid="form-viewer-title">
              {form.name}
            </h2>
            {canEdit && (
              <Button
                size="sm"
                variant={mode === 'edit' ? 'default' : 'outline'}
                className={mode === 'edit' ? 'bg-[#003366] text-white' : ''}
                onClick={() => setMode(mode === 'edit' ? 'preview' : 'edit')}
                data-testid="form-viewer-edit-btn"
              >
                {mode === 'edit' ? <Eye className="w-3.5 h-3.5 mr-1.5" /> : <Pencil className="w-3.5 h-3.5 mr-1.5" />}
                {mode === 'edit' ? 'View PDF' : 'Edit'}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleDownload} disabled={!pdfUrl} data-testid="form-viewer-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="bg-[#003366] hover:bg-[#004080] text-white" disabled={busy} data-testid="form-viewer-send-btn">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Send
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleSendEmail} data-testid="form-viewer-send-email">
                  <Mail className="w-3.5 h-3.5 mr-2 text-blue-600" /> Email
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openSmsDialog} data-testid="form-viewer-send-text">
                  <MessageSquare className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Text (SMS)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openSignDialog} data-testid="form-viewer-send-sign-link">
                  <FileSignature className="w-3.5 h-3.5 mr-2 text-violet-600" /> Online Signature
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handlePrint} data-testid="form-viewer-print">
                  <Printer className="w-3.5 h-3.5 mr-2 text-gray-600" /> Print
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onClose} data-testid="form-viewer-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Barra de estados propia de esta forma */}
          <div className="flex items-center gap-1.5 px-4 py-2 border-b border-gray-100 bg-gray-50/70 overflow-x-auto flex-shrink-0" data-testid="form-viewer-stepper">
            {statuses.map((s, idx) => {
              const curIdx = statuses.findIndex(x => x.key === status);
              const isCurrent = s.key === status;
              const isPast = curIdx > -1 && idx < curIdx;
              return (
                <div key={s.key} className="flex items-center flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleStatusClick(s.key)}
                    disabled={busy}
                    data-testid={`form-viewer-status-${s.key}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors border ${
                      isCurrent ? 'bg-[#003366] text-white border-[#003366] shadow-sm'
                        : isPast ? 'bg-blue-50 text-[#003366] border-blue-100 hover:bg-blue-100'
                          : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-100 hover:text-gray-600'
                    }`}
                  >
                    {formStatusLabel(s)}
                  </button>
                  {idx < statuses.length - 1 && <div className={`w-3 h-[2px] flex-shrink-0 ${isPast ? 'bg-[#003366]/40' : 'bg-gray-200'}`} />}
                </div>
              );
            })}
          </div>

          {/* PDF a pantalla completa — sin paneles a los costados */}
          <div className="flex-1 overflow-hidden bg-gray-200">
            {mode === 'preview' ? (
              pdfLoading ? (
                <div className="h-full flex items-center justify-center text-gray-400"><Loader2 className="w-7 h-7 animate-spin" /></div>
              ) : pdfError ? (
                <div className="h-full flex items-center justify-center text-gray-500 text-sm" data-testid="form-viewer-pdf-error">
                  {t('permits.battery.previewError', 'No se pudo generar el PDF de esta forma')}
                </div>
              ) : pdfUrl ? (
                <iframe src={pdfUrl} title={form.name} className="w-full h-full border-0" data-testid="form-viewer-pdf" />
              ) : null
            ) : isStructured && !officialMeta?.official ? (
              <div className="h-full overflow-y-auto bg-white p-4" data-testid="form-viewer-edit-panel">{editChildren}</div>
            ) : (
              <div className="h-full bg-white" data-testid="form-viewer-edit-panel">
                <FormFiller
                  templateId={isStructured ? officialMeta?.template_id : form.templateId}
                  instanceId={isStructured ? officialMeta?.instance_id : instanceId}
                  linkedType="permit"
                  linkedId={permit?.id}
                  context={{ permit_id: permit?.id, project_id: permit?.project_id }}
                  onSaved={() => { setMode('preview'); onChanged && onChanged(); }}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Send > Text: teléfono + mensaje editable con link tokenizado al PDF */}
      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent className="sm:max-w-[460px]" data-testid="form-viewer-sms-dialog">
          <div className="space-y-3 pt-2">
            <h3 className="text-base font-bold font-['Barlow']">Enviar por Text (SMS)</h3>
            <div className="space-y-1.5">
              <Label className="text-xs">Teléfono</Label>
              <Input value={smsTo} onChange={e => setSmsTo(e.target.value)} placeholder="+1 555 123 4567" data-testid="form-viewer-sms-to" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mensaje</Label>
              <Textarea value={smsBody} onChange={e => setSmsBody(e.target.value)} rows={4} data-testid="form-viewer-sms-body" />
              <p className="text-[10px] text-gray-400">El link es único y no adivinable — sirve el PDF de esta forma.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSmsOpen(false)}>Cancelar</Button>
              <Button onClick={handleSendSms} disabled={smsSending} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="form-viewer-sms-send-btn">
                {smsSending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Enviar SMS
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send > Online Signature: public link with a unique token, copyable or sendable by SMS */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="sm:max-w-[460px]" data-testid="form-viewer-sign-dialog">
          <div className="space-y-3 pt-2">
            <h3 className="text-base font-bold font-['Barlow'] flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-violet-600" /> Online Signature
            </h3>
            <p className="text-xs text-gray-500">The client opens this link, views the PDF, and signs with their finger/mouse. Once signed, the status automatically flips to "Signed" and you get notified.</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Unique signature link</Label>
              <div className="flex gap-2">
                <Input value={signLink} readOnly className="text-xs font-mono" data-testid="form-viewer-sign-link-input" />
                <Button size="icon" variant="outline" onClick={copySignLink} data-testid="form-viewer-sign-link-copy-btn">
                  {signCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-1.5 pt-1 border-t border-gray-100">
              <Label className="text-xs">Send by SMS (optional)</Label>
              <div className="flex gap-2">
                <Input value={signSmsTo} onChange={e => setSignSmsTo(e.target.value)} placeholder="+1 555 123 4567" data-testid="form-viewer-sign-sms-to" />
                <Button onClick={sendSignLinkSms} disabled={signSending} className="bg-[#003366] hover:bg-[#004080] text-white flex-shrink-0" data-testid="form-viewer-sign-sms-send-btn">
                  {signSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send'}
                </Button>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setSignOpen(false)} data-testid="form-viewer-sign-close-btn">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
